import { MetricModel, MetricApiResponse, MetricBackendDependencies, MetricBackendError, TimezoneOffset } from "./types";

/**
 * MetricBackendService - TypeScript implementation
 *
 * This is a framework-independent TypeScript implementation of the metric backend service,
 * migrated from the Angular version for better type safety and maintainability.
 */
export class MetricBackendService {
  private backendApi: any;
  private ApiPath: any;
  private Metric: any;
  private MetricSparkline: any;
  private MetricSummary: any;

  constructor(dependencies: MetricBackendDependencies) {
    this.backendApi = dependencies.backendApi;
    this.ApiPath = dependencies.ApiPath;
    this.Metric = dependencies.Metric;
    this.MetricSparkline = dependencies.MetricSparkline;
    this.MetricSummary = dependencies.MetricSummary;
  }

  /**
   * Request a single metric by ID
   */
  metricRequested(metricId: string) {
    try {
      if (!this.ApiPath) {
        throw new Error("ApiPath is undefined in React MetricBackendService");
      }

      const url = this.ApiPath.metric.getMetricUrl(metricId);

      return this.backendApi
        .get(url)
        .then((response: MetricApiResponse) => {
          if (response.data) {
            const metric = this.Metric.fromJson(response.data);
            return metric;
          }

          throw new Error("No data received from metric request");
        })
        .catch((error: any) => {
          throw this.handleError("metricRequested", error);
        });
    } catch (error) {
      throw this.handleError("metricRequested", error);
    }
  }

  /**
   * Create a new metric
   */
  async metricCreated(metric: MetricModel): Promise<MetricModel> {
    const url = this.ApiPath.metric.createMetricUrl();
    const data = {
      metric: metric.toJson(),
    };

    try {
      const response: MetricApiResponse = await this.backendApi.post(url, {}, data);

      if (response.data) {
        return this.Metric.fromJson(response.data);
      }

      throw new Error("No data received from metric creation");
    } catch (error) {
      throw this.handleError("metricCreated", error);
    }
  }

  /**
   * Update an existing metric
   */
  async metricChanged(metric: MetricModel): Promise<MetricModel> {
    const data = {
      metric: metric.toJson(),
    };
    const url = this.ApiPath.metric.getMetricUrl(metric.id());

    try {
      const response: MetricApiResponse = await this.backendApi.put(url, {}, data);

      if (response.data) {
        return this.Metric.fromJson(response.data);
      }

      throw new Error("No data received from metric update");
    } catch (error) {
      throw this.handleError("metricChanged", error);
    }
  }

  /**
   * Delete a metric
   */
  async metricDeleted(metric: MetricModel): Promise<void> {
    const url = this.ApiPath.metric.getMetricUrl(metric.id());

    try {
      await this.backendApi.delete(url);
    } catch (error) {
      throw this.handleError("metricDeleted", error);
    }
  }

  /**
   * Request all metrics
   */
  async metricsRequested(): Promise<MetricModel[]> {
    const url = this.ApiPath.metric.getMetricsUrl();

    try {
      const response: MetricApiResponse = await this.backendApi.get(url);

      if (response.data) {
        return response.data.map((metricData: any) => {
          return this.Metric.fromJson(metricData);
        });
      }

      throw new Error("No data received from metrics request");
    } catch (error) {
      throw this.handleError("metricsRequested", error);
    }
  }

  /**
   * Request metric summary data
   */
  async metricSummaryRequested(metric: MetricModel): Promise<any> {
    const url = this.ApiPath.metric.getMetricSummaryUrl();
    const data = {
      metric: metric.toJson(),
    };

    try {
      const response: MetricApiResponse = await this.backendApi.post(url, {}, data);

      if (response.data) {
        return this.MetricSummary.fromJson(metric, response.data);
      }

      throw new Error("No data received from metric summary request");
    } catch (error) {
      throw this.handleError("metricSummaryRequested", error);
    }
  }

  /**
   * Request metric sparkline data
   */
  async metricSparklineRequested(
    metric: MetricModel,
    timezoneOffset?: TimezoneOffset,
    controlTestId?: string,
    globalFilters?: any
  ): Promise<any> {
    const url = this.ApiPath.metric.getMetricTimelineUrl(metric.id());
    const utcOffsetString = timezoneOffset ? timezoneOffset.utcOffsetString() : undefined;
    const data = {
      metric: metric.toJson(),
      globalFilters,
      timezone_offset: utcOffsetString,
    };

    try {
      const response: MetricApiResponse = await this.backendApi.post(url, {}, data);

      if (response.data) {
        return this.MetricSparkline.fromJson(response.data);
      }

      throw new Error("No data received from metric sparkline request");
    } catch (error) {
      throw this.handleError("metricSparklineRequested", error);
    }
  }

  /**
   * Handle and format errors consistently
   */
  private handleError(operation: string, error: any): Error {
    const errorMessage = error?.message || error?.toString() || "Unknown error occurred";

    // Return a proper Error object with formatted message
    const formattedError = new Error(`MetricBackendService.${operation}: ${errorMessage}`);

    // Preserve additional properties if they exist
    if (error?.code) (formattedError as any).code = error.code;
    if (error?.status) (formattedError as any).status = error.status;

    return formattedError;
  }
}
